import { Injectable, inject, signal } from '@angular/core';
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHintALLOption } from '@capacitor/barcode-scanner';
import { Camera } from '@capacitor/camera';
import { BrowserCodeReader, BrowserMultiFormatReader } from '@zxing/browser';
import { NavController } from '@ionic/angular/standalone';
import { Job } from '../models/job.model';
import { JobsService } from './jobs.service';
import { PlatformService } from './platform.service';
import { ToastService } from '../../shared/services/toast.service';
import { CAM_PERM_MSG, CAM_PERM_MSG_SHORT } from '../constants/app.messages';

/** Lifecycle state of an in-progress or completed scan. */
export type ScanState = 'idle' | 'scanning' | 'success' | 'error';

/**
 * Sentinel error message used by {@link ScanService.tryNative} to distinguish
 * a JS-level native-scan timeout from any other native rejection.
 */
const NATIVE_SCAN_TIMEOUT_ERROR = 'native-scan-timeout';

/**
 * Hard JS-level ceiling on how long `tryNative()` waits for
 * `CapacitorBarcodeScanner.scanBarcode()` to settle.
 *
 * MOB-293: the native scanner's top-right "X" has been observed to dismiss
 * the camera view without driving the plugin's underlying promise to
 * resolve/reject — unlike its own bottom "Cancel" control, which does. Without
 * this safety net, `scan()`'s `await this.tryNative()` (and therefore every
 * consumer's `scanOpen` signal) can be stuck in `'scanning'` forever even
 * though the native camera view is already gone, leaving
 * `ScanOverlayComponent`'s own hint/Cancel chrome stranded on top of
 * whatever screen loads next.
 *
 * Deliberately generous — this is NOT a "scans shouldn't take longer than
 * this" assumption; a user fumbling with a damaged/poorly-lit barcode can
 * legitimately take a while. It only bounds the case where the native
 * promise never settles at all. Revisit after device verification (see
 * CLAUDE.md's MOB-293 note).
 */
const NATIVE_SCAN_TIMEOUT_MS = 90_000;

/**
 * Reason a scan overlay closed without a successful result.
 * Defined here so both the overlay component and the service share one type.
 */
export type ScanCloseReason = 'cancelled' | 'error';

/**
 * The resolved value of a successful scan.
 *
 * @property text     - Raw barcode text decoded from the image.
 * @property platform - Which scanner engine produced the result.
 */
export interface ScanResult {
  text:     string;
  platform: 'native' | 'zxing';
}

/**
 * Unified barcode-scanning service.
 *
 * @remarks
 * Uses `@capacitor/barcode-scanner` as the primary engine on native iOS/Android.
 * It opens a native scanning UI via a single `scan()` call that resolves when a
 * barcode is detected or the user dismisses. Falls back to `@zxing/browser`
 * (`BrowserMultiFormatReader`) on web / PWA where Capacitor native plugins
 * are unavailable.
 *
 * **Native flow (iOS/Android):**
 * 1. {@link ensureCameraPermission} explicitly checks/requests camera access via
 *    `@capacitor/camera` — `@capacitor/barcode-scanner`'s own plugin interface
 *    exposes no `checkPermissions`/`requestPermissions` of its own (only
 *    `scanBarcode()`), so this reuses the same OS-level camera permission
 *    `PhotoService` already checks for photo capture, since both plugins gate
 *    the identical native camera hardware permission. A permanently-denied
 *    result skips opening the native scanner entirely rather than opening a
 *    camera view that can only fail.
 * 2. `CapacitorBarcodeScanner.scanBarcode({ hint: ALL })` — opens the native scanning UI;
 *    resolves with `{ ScanResult: string }`.
 * 3. Raced against {@link NATIVE_SCAN_TIMEOUT_MS} (MOB-293) so a native promise that
 *    never settles (observed on the scanner's top-right "X" dismiss) can't block
 *    `scan()` forever. A native failure — including this timeout — never falls
 *    through to the ZXing path, since the native-path template renders no `<video>`
 *    element for it to use.
 *
 * **Usage:**
 * 1. Call `scan(videoElement?)` — pass a `<video>` element for the ZXing path.
 * 2. `scanState` and `lastResult` signals update reactively.
 * 3. Call `stopScan()` in the consuming component's `ngOnDestroy` to release
 *    the camera when the scan screen is dismissed mid-scan.
 *
 * @example
 * ```ts
 * const scanService = inject(ScanService);
 * const result = await scanService.scan(this.videoEl?.nativeElement);
 * if (result) { // navigate to next step }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class ScanService {
  private readonly platform     = inject(PlatformService);
  private readonly jobsService  = inject(JobsService);
  private readonly navCtrl      = inject(NavController);
  private readonly toastService = inject(ToastService);

  // ─── Global overlay state ─────────────────────────────────────────────────

  /** `true` while the global scan overlay is visible. */
  readonly scanOpen = signal(false);

  /**
   * `true` when the user has permanently denied camera access and the OS
   * will no longer show the permission dialog. Set by
   * {@link ensureCameraPermission}; reset to `false` once permission is
   * granted again.
   *
   * @remarks
   * Mirrors `GpsService.permissionDenied` — exposed so a consumer (e.g. a
   * future "Open Settings" prompt) can react without having to infer denial
   * from the generic `'error'` scan state.
   */
  readonly permissionDenied = signal(false);

  /**
   * Opens the global scan overlay.
   * Stops any in-flight scan first (e.g. a per-defendant document scan in
   * `VerifyComponent`) so only one camera stream is ever active at a time.
   */
  openScan(): void {
    if (this.scanning) this.stopScan();
    this.scanOpen.set(true);
  }

  /**
   * Called by the global overlay when a barcode is successfully decoded.
   * Closes the overlay, resolves the scanned text to a job, and navigates.
   * Sign-ready jobs navigate to `/sign?highlight=id` so the Sign Queue scrolls
   * directly to the matching card.
   *
   * @param result - The decoded barcode value.
   */
  async onScanResult(result: ScanResult): Promise<void> {
    this.scanOpen.set(false);
    const text = result.text.trim();
    const job  = this.jobsService.findByJobNum(text) ?? this.jobsService.getJob(text);
    if (!job) {
      await this.toastService.error('No matching job found for scanned code.');
      return;
    }
    const { route, queryParams } = this.routeForJob(job);
    if (queryParams) {
      void this.navCtrl.navigateRoot(route, { queryParams });
    } else {
      void this.navCtrl.navigateRoot(route);
    }
  }

  /**
   * Called by the global overlay when it closes without a result.
   * Shows a permission toast on error; silent on cancel.
   *
   * @param reason - `'cancelled'` or `'error'`.
   */
  async onScanClosed(reason: ScanCloseReason): Promise<void> {
    this.scanOpen.set(false);
    if (reason === 'error') {
      const msg = this.platform.isNative ? CAM_PERM_MSG : CAM_PERM_MSG_SHORT;
      await this.toastService.warning(msg);
    }
  }

  // ─── Route helper ─────────────────────────────────────────────────────────

  /**
   * Returns the target route and optional query params for a job based on its workflow state.
   * Sign-ready jobs include a `highlight` param so the Sign Queue scrolls to the card.
   *
   * @param job - The resolved job.
   * @returns Route segments and optional query params object.
   */
  private routeForJob(job: Job): { route: string[]; queryParams?: Record<string, string> } {
    if (!job.accepted) return { route: ['/accept'] };
    const isSignReady =
      (job.esignStatus === 'N' || job.esignStatus === 'R') &&
      job.approvedStatus === 0 && job.manner > 0 && job.jobStatus === 1;
    if (isSignReady) return { route: ['/sign'], queryParams: { highlight: job.id } };
    return { route: ['/verify', job.id] };
  }

  /** Current lifecycle state — `'idle'` at startup, transitions through `'scanning'` to `'success'` or `'error'`. */
  readonly scanState = signal<ScanState>('idle');

  /** Most recent successful scan result; `null` until the first success. */
  readonly lastResult = signal<ScanResult | null>(null);

  /**
   * Tracks the active `<video>` element so `releaseCamera()` can stop its
   * `MediaStream` tracks and turn off the camera indicator light.
   */
  private activeVideoEl: HTMLVideoElement | null = null;

  /**
   * Active ZXing reader instance.
   * Stored so `stopScan()` / `scan()` can call `resetZxingReader()` to release
   * the camera stream managed internally by ZXing — stopping the video
   * element's `srcObject` tracks alone is not sufficient.
   */
  private zxingReader: BrowserMultiFormatReader | null = null;

  /**
   * `true` while a `scan()` call is in-flight.
   * Set to `false` by `stopScan()` so `scan()` can distinguish a user
   * cancel from a genuine decode failure and skip the error-state transition.
   */
  private scanning = false;

  /**
   * Monotonically-increasing counter incremented each time a new scan starts
   * or `stopScan()` is called. Each `scan()` call captures its own snapshot
   * (`gen`) and bails out silently when the snapshot no longer matches
   * `scanGeneration` — meaning a newer scan or an explicit cancel superseded it.
   */
  private scanGeneration = 0;

  /**
   * Starts a barcode scan, trying the native scanner first (bounded by
   * {@link NATIVE_SCAN_TIMEOUT_MS}, MOB-293) and falling back to ZXing on
   * web/PWA only — a native failure (including a timeout) never falls
   * through to ZXing, since there is no `<video>` element on the native path.
   *
   * If a previous ZXing scan is in progress its reader is reset before
   * starting. If a native scan is in progress, `stopScan()` is awaited first.
   *
   * @param videoElement - Optional `<video>` element for the ZXing preview
   *                       stream. Ignored on native.
   * @returns The first decoded barcode, or `null` if cancelled / failed.
   */
  async scan(videoElement?: HTMLVideoElement): Promise<ScanResult | null> {
    if (this.zxingReader) {
      this.resetZxingReader();
      BrowserCodeReader.releaseAllStreams();
      this.releaseCamera();
    } else if (this.scanning) {
      await this.stopNative();
    }

    const gen = ++this.scanGeneration;
    this.scanning = true;
    this.scanState.set('scanning');

    const native = await this.tryNative();
    if (!this.scanning || this.scanGeneration !== gen) return null;

    // ZXing is web/PWA-only — the native-path template renders no <video>
    // element (see ScanOverlayComponent), so calling it here would pass an
    // undefined video device into ZXing, which can itself hang/misbehave on
    // native and defeat the timeout guarantee in tryNative() (MOB-293).
    const result = native ?? (this.platform.isNative ? null : await this.tryZXing(videoElement));
    if (!this.scanning || this.scanGeneration !== gen) return null;
    this.scanning = false;
    if (result) {
      this.lastResult.set(result);
      this.scanState.set('success');
    } else {
      this.scanState.set('error');
    }
    return result;
  }

  /**
   * Stops any active scan, restores the WebView background (native),
   * releases the camera, and resets `scanState` to `'idle'`.
   */
  stopScan(): void {
    this.scanning = false;
    this.scanGeneration++;
    void this.stopNative();
    this.resetZxingReader();
    BrowserCodeReader.releaseAllStreams();
    this.releaseCamera();
    this.scanState.set('idle');
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  /**
   * No-op — the official `@capacitor/barcode-scanner` native UI self-dismisses
   * when a barcode is found or the user cancels. No teardown calls required.
   */
  private async stopNative(): Promise<void> {
    // intentional no-op
  }

  /**
   * Checks the current camera permission state and requests it if not yet
   * determined, via `@capacitor/camera`.
   *
   * @remarks
   * `@capacitor/barcode-scanner`'s own `CapacitorBarcodeScannerPlugin`
   * interface declares only `scanBarcode()` — no `checkPermissions`/
   * `requestPermissions` of its own — so it was previously relying entirely
   * on whatever implicit OS prompt the native scanner triggers internally,
   * with no way for this app to detect a denial ahead of time or skip
   * opening a camera view that can only fail. This reuses `@capacitor/camera`
   * instead: both plugins gate the identical native camera hardware
   * permission, and `PhotoService.ensurePhotosPermission()` already checks
   * it the same way for photo capture.
   *
   * State mapping mirrors `GpsService.ensurePermission()`:
   * - `'granted'` → returns `true` immediately (no dialog).
   * - `'denied'`  → returns `false` (permanently denied; OS will not show
   *                 the dialog again).
   * - `'prompt'` / `'prompt-with-rationale'` → shows the OS dialog; returns
   *                 `true` if the user grants, `false` otherwise.
   *
   * A thrown/unavailable check (e.g. plugin not ready) falls back to `true`
   * so `tryNative()` still opens the native scanner and lets it surface its
   * own prompt, rather than blocking a scan over an inconclusive check.
   *
   * @returns `true` when camera access is available or was just granted;
   *          `false` when permanently denied.
   */
  private async ensureCameraPermission(): Promise<boolean> {
    try {
      const { camera } = await Camera.checkPermissions();
      if (camera === 'granted') { this.permissionDenied.set(false); return true; }
      if (camera === 'denied')  { this.permissionDenied.set(true);  return false; }
      const { camera: result } = await Camera.requestPermissions({ permissions: ['camera'] });
      const granted = result !== 'denied';
      this.permissionDenied.set(!granted);
      return granted;
    } catch {
      this.permissionDenied.set(false);
      return true; // check unavailable — let the native scanner surface its own prompt
    }
  }

  /**
   * Attempts a one-shot scan via `CapacitorBarcodeScanner.scanBarcode`.
   * Explicitly checks/requests camera permission first via
   * {@link ensureCameraPermission} — a permanently-denied result skips
   * opening the native scanning UI entirely and returns `null` immediately,
   * without ever calling `scanBarcode()`.
   *
   * Once permission is confirmed, races the native call against
   * {@link NATIVE_SCAN_TIMEOUT_MS} (MOB-293) so a hung native promise —
   * observed after dismissing via the scanner's top-right "X" — can never
   * block `scan()` forever. On timeout, logs a distinguishable
   * `console.warn` so a device test can confirm whether this path (rather than a
   * normal permission/decode failure) actually fired.
   *
   * Returns `null` on web, on a permanently-denied permission, or on any
   * native error (generic failure or this timeout) — all collapse to the
   * same `null` return value for `scan()`'s state machine; only the
   * diagnostic differs.
   */
  private async tryNative(): Promise<ScanResult | null> {
    if (!this.platform.isNative) return null;

    const permitted = await this.ensureCameraPermission();
    if (!permitted) {
      console.warn('[ScanService] Camera permission denied — not opening native scanner.');
      return null;
    }

    try {
      const result = await Promise.race([
        CapacitorBarcodeScanner.scanBarcode({
          hint: CapacitorBarcodeScannerTypeHintALLOption.ALL,
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error(NATIVE_SCAN_TIMEOUT_ERROR)), NATIVE_SCAN_TIMEOUT_MS),
        ),
      ]);
      return result.ScanResult ? { text: result.ScanResult, platform: 'native' } : null;
    } catch (err) {
      if (err instanceof Error && err.message === NATIVE_SCAN_TIMEOUT_ERROR) {
        console.warn(
          `[ScanService] native scanBarcode() did not settle within ${NATIVE_SCAN_TIMEOUT_MS}ms — ` +
            'possible X-button dismiss not reaching the native scanner library (MOB-293).',
        );
      }
      return null;
    }
  }

  /**
   * Attempts a one-shot scan using ZXing `BrowserMultiFormatReader`.
   * Always releases the camera in the `finally` block.
   *
   * @param videoElement - Optional `<video>` element for the preview stream.
   */
  private async tryZXing(videoElement?: HTMLVideoElement): Promise<ScanResult | null> {
    this.activeVideoEl = videoElement ?? null;
    this.zxingReader = new BrowserMultiFormatReader();
    try {
      const result = await this.zxingReader.decodeOnceFromVideoDevice(undefined, videoElement);
      return { text: result.getText(), platform: 'zxing' };
    } catch {
      return null;
    } finally {
      this.resetZxingReader();
      this.releaseCamera();
    }
  }

  /**
   * Resets the ZXing reader (if any) and clears the stored reference.
   * `reset()` releases ZXing's internal `getUserMedia` stream — stopping
   * video element tracks alone is insufficient to turn off the camera light.
   */
  private resetZxingReader(): void {
    if (!this.zxingReader) return;
    const reader = this.zxingReader as unknown as Record<string, unknown>;
    if (typeof reader['reset'] === 'function') {
      (reader['reset'] as () => void)();
    }
    this.zxingReader = null;
  }

  /**
   * Stops all `MediaStream` tracks on the active video element and clears its
   * `srcObject` — turns off the camera hardware indicator light.
   */
  private releaseCamera(): void {
    const el = this.activeVideoEl;
    const stream = el?.srcObject;
    if (stream && typeof (stream as MediaStream).getTracks === 'function') {
      (stream as MediaStream).getTracks().forEach(t => t.stop());
      el!.srcObject = null;
    }
    this.activeVideoEl = null;
  }
}
